import React, { useState } from "react";

import { Grid, Modal } from "@mui/material";
import { AddCircleOutlineOutlined as AddCircleOutlineOutlinedIcon } from "@mui/icons-material";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import useActionAutomaticSequence from "../../../../../../../hooks/useActionAutomaticSequence";
import { AutomaticSequencing } from "../../../../../../../models/automatic.sequencing";
import { Contact } from "../../../../../../../models/contact";
import ContactService from "../../../../../../../services/contact.service";
import { keysToCamel } from "../../../../../../../util/convertCamelCase";
import { AutomaticSequenceOptions } from "../../../../../../../util/enums";
import Button, { Color } from "../../../../../../../components/Form/Button";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import AddContactModal from "./AddContactModal";
import ContactsList from "./components/ContactsList";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import ContactsModal from "./ContactsModal";
import useStyles from "./styles";

interface Props {
  companyContacts?: Contact[];
  refetchCompanyContacts?: () => void;
  permission?: {
    edit: boolean
  }
}

export const FormContact = (props: Props) => {
  const { refetchCompanyContacts, companyContacts, permission } = props;

  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { getAutomaticSequenceByType, increaseAutomaticSequence, isAutomaticSequenceReference } =
    useActionAutomaticSequence();

  const [contact, setContact] = useState<Contact | undefined>(undefined);
  const [showAddContact, setShowAddContact] = useState(false);
  const [showDeleteContact, setShowDeleteContact] = useState(false);
  const [showContacts, setShowContacts] = useState(false);
  const [sequence, setSequence] = useState<AutomaticSequencing>();

  getAutomaticSequenceByType(AutomaticSequenceOptions.USER, showAddContact, setSequence);

  const { mutate: updateContact, isLoading: isUpdating } = useMutation(
    ["contact.update"],
    ({ id, contact }: { id: number; contact: Contact }) => ContactService.update(id, contact),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        if (refetchCompanyContacts) refetchCompanyContacts();
        setShowAddContact(false);
        setShowDeleteContact(false);
      },
    }
  );

  const { mutate: createContact, isLoading: isCreating } = useMutation(
    ["contact.create"],
    (contact: Contact) => ContactService.create(contact),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        if (refetchCompanyContacts) refetchCompanyContacts();
        setShowAddContact(false);
      },
    }
  );

  const handleConfirmDeleteContact = (contact: Contact) => {
    setContact(keysToCamel(contact));
    setShowDeleteContact(true);
  };

  const handleEditContact = (contact: Contact) => {
    setContact(keysToCamel(contact));
    setShowAddContact(true);
  };

  const setHandleAddContact = () => {
    setContact(undefined);
    setShowAddContact(true);
  };

  const handleDeleteContact = (contact: Contact) => {
    if (contact && contact.id) {
      if (contact.userId) {
        const deletedUserContact = {
          ...contact,
          user: { ...contact.user, status: "Inactive" },
        } as Contact;

        if (deletedUserContact.id)
          updateContact({
            id: deletedUserContact.id,
            contact: deletedUserContact,
          });
      } else {
        const deletedContact = { ...contact, valid: false };

        if (deletedContact.id) updateContact({ id: deletedContact.id, contact: deletedContact });
      }
    }
  };

  const handleCreateContact = (contact: Contact) => {
    let hasCodeChange: boolean = false;

    if (contact?.user) {
      const sequenceCode = `${sequence?.prefix ? sequence?.prefix : ""}${
        sequence?.nextValue ? sequence?.nextValue : ""
      }`;

      if (contact?.user.code !== sequenceCode) hasCodeChange = true;
    }

    createContact(contact);

    if (!hasCodeChange && sequence && sequence?.id && contact?.user && contact?.user?.code) {
      if (isAutomaticSequenceReference(sequence, contact?.user?.code))
        increaseAutomaticSequence({
          id: sequence?.id,
          sequence,
          value: contact?.user?.code,
        });
    }
  };

  return (
    <div className={classes.mainDiv}>
      <Modal
        open={showContacts}
        onClose={() => setShowContacts(false)}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        data-cy="businessContactsListModal"
      >
        <div>
          <ContactsModal
            handleCloseModal={() => setShowContacts(false)}
            contacts={companyContacts ? [...companyContacts].reverse() : []}
            refetchCompanyContacts={() => refetchCompanyContacts && refetchCompanyContacts()}
            permission={permission}
          />
        </div>
      </Modal>

      {showAddContact && (
        <AddContactModal
          open={showAddContact}
          handleClose={() => setShowAddContact(false)}
          handleUpdateOrCreateContact={(contact) => {
            if (contact.id) updateContact({ id: contact.id, contact });
            else {
              handleCreateContact(contact);
            }
          }}
          isLoading={isUpdating || isCreating}
          selectedContact={contact}
          sequence={sequence}
        />
      )}

      <Modal
        open={showDeleteContact}
        onClose={() => setShowDeleteContact(false)}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        style={{ overflowY: "scroll" }}
      >
        <div>
          <ConfirmDeleteModal
            handleClose={() => setShowDeleteContact(false)}
            handleDeleteContact={(contact) => handleDeleteContact(contact)}
            isLoadingDelete={isUpdating}
            contact={contact}
          />
        </div>
      </Modal>

      <div className={classes.title}>
        <span className={classes.businessLabel}>{translate("businessContacts")}</span>
        <span className={classes.contactsCount}>
          {companyContacts && companyContacts?.length > 0 ? companyContacts?.length : 0}
        </span>
      </div>
      <div className={classes.divColumn}>
        {companyContacts && (
          <ContactsList
            contacts={keysToCamel(
              companyContacts?.length <= 3
                ? [...companyContacts].reverse()
                : [...companyContacts].slice(-3).reverse()
            )}
            onSelectDeleteContact={(contact) => handleConfirmDeleteContact(contact)}
            onSelectEditContact={(contact) => handleEditContact(contact)}
            permission={permission}
          />
        )}
      </div>

      <Grid item xs={12} className={classes.buttonsContainer}>
        {
          (permission && permission.edit) && <Button
            className={classes.buttonAdd}
            customColor={Color.SECONDARY}
            data-cy="addAnotherContact"
            type="button"
            onClick={() => setHandleAddContact()}
          >
            <AddCircleOutlineOutlinedIcon fontSize="small" className={classes.userPlus} />
            <strong>{translate("addContact")}</strong>
          </Button>
        }
        <Button
          className={classes.buttonViewAll}
          customColor={Color.INFO}
          data-cy="viewAllContacts"
          type="button"
          onClick={() => setShowContacts(true)}
        >
          <strong>{translate("viewAllContacts")}</strong>
        </Button>
      </Grid>
    </div>
  );
};
